import React, { useState } from 'react';
import { Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination, Typography, Box, Button, Skeleton, useTheme } from '@mui/material';
import { InboxOutlined } from '@mui/icons-material';
import { getBrandedTableHeadSx } from '../../utils/colorUtils';
import { fontSize, fontWeight } from '../../utils/accounting/styles';

const DataTable = ({
  columns = [],
  data = [],
  loading = false,
  onRowClick,
  emptyMessage = 'No data found.',
  emptyAction,
  summaryRow,
  pagination = true,
  pageSize = 20,
}) => {
  const theme = useTheme();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(pageSize);

  const paginatedData = pagination ? data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : data;

  return (
    <Paper variant="outlined">
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={getBrandedTableHeadSx(theme)}>
              {columns.map((col) => (
                <TableCell
                  key={col.key}
                  align={col.align || 'left'}
                  sx={{ fontSize: fontSize.xs, fontWeight: fontWeight.semibold, width: col.width, py: 0.75, lineHeight: 1.3 }}
                >
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              [...Array(5)].map((_, i) => (
                <TableRow key={i}>
                  {columns.map((col) => (
                    <TableCell key={col.key}><Skeleton /></TableCell>
                  ))}
                </TableRow>
              ))
            ) : paginatedData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center" sx={{ py: 6 }}>
                  <InboxOutlined sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                  <Typography color="text.secondary" sx={{ fontSize: fontSize.sm, mb: 1 }}>
                    {emptyMessage}
                  </Typography>
                  {emptyAction && (
                    <Button variant="contained" size="small" onClick={emptyAction.onClick} sx={{ textTransform: 'none' }}>
                      {emptyAction.label}
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ) : (
              <>
                {paginatedData.map((row, i) => (
                  <TableRow
                    key={row.id || i}
                    hover
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    sx={{ cursor: onRowClick ? 'pointer' : 'default' }}
                  >
                    {columns.map((col) => (
                      <TableCell key={col.key} align={col.align || 'left'} sx={{ fontSize: '0.8125rem', py: 0.75, lineHeight: 1.4 }}>
                        {col.render ? col.render(row) : row[col.key]}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
                {summaryRow && (
                  <TableRow sx={{ bgcolor: 'action.hover' }}>
                    {columns.map((col, i) => (
                      <TableCell key={col.key} align={col.align || 'left'} sx={{ fontSize: '0.8125rem', fontWeight: fontWeight.semibold, py: 0.75 }}>
                        {i === 0 ? summaryRow.label : (summaryRow.values?.[col.key] != null ? summaryRow.values[col.key] : '')}
                      </TableCell>
                    ))}
                  </TableRow>
                )}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {pagination && data.length > 0 && (
        <TablePagination
          component="div"
          count={data.length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[20, 50, 100]}
          sx={{ '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': { fontSize: fontSize.xs } }}
        />
      )}
    </Paper>
  );
};

export default DataTable;
